import { useApolloClient, useMutation, useQuery } from "@apollo/client";

import GET_OLA_LISTING from "../../../graphql/queries/ola/getOlaListing";
import GET_COUNTRIES from "../../../graphql/queries/registration/getCountries";
import GET_LA_COUNT from "../../../graphql/queries/ola/laCount";
import GET_SHORT_TERM_LISTING from "../../../graphql/queries/short-term/getShortTermListing";
import GET_SHORT_TERM_LA_COUNT from "../../../graphql/queries/short-term/getShortTermCount";
import GET_COSES from "../../../graphql/queries/ola/getCoses";
import CREATE_COS from "../../../graphql/mutations/ola/createCos";
import UPDATE_COS from "../../../graphql/mutations/ola/updateCos";

import CustomTable from "../../../components/Table";
import CustomButton from "../../../components/CustomButton";
import CertificatesModal from "./CertificatesModal";
import ExportCosPdf from "./ExportCosPdf";
import TableSkeleton from "../../../components/Table/components/TableSkeleton";
import Filters from "../../../components/Filters";

import { useEffect, useMemo, useRef, useState } from "react";
import { useOlaFilters } from "../../LearningAgreements/Listing/hooks/useOlaFilters";

import { useTranslation } from "react-i18next";
import { ToastContainer, toast } from "react-toastify";
import toastConfiguration from "../../../data/toastConfiguration";
import "react-toastify/dist/ReactToastify.css";
import "./index.scss";

const STATUS_OPTIONS = [
  "preliminary_signed-by-student-sending-receiving",
  "final_signed-by-student-sending-receiving",
];

const LOCAL_STORAGE_KEYS = {
  page: "pageCertificatesArrivalDepartures",
  sortBy: "sortByCertificatesArrivalDepartures",
  filters: "selectedFiltersCertificatesArrivalDepartures",
};

const academicFilter = () =>
  new Date().getMonth() < 6
    ? new Date().getFullYear() - 1 + "/" + new Date().getFullYear()
    : new Date().getFullYear() + "/" + (new Date().getFullYear() + 1);

const buildDefaultFilters = () => ({
  erasmusCode: "",
  country: [],
  academicYear: academicFilter(),
  studentFirstName: "",
  studentLastName: "",
  organizationalUnit: "",
  iscedCodeLA: "",
});

const parseStoredFilters = () => {
  const stored = localStorage.getItem(LOCAL_STORAGE_KEYS.filters);
  if (!stored) {
    return buildDefaultFilters();
  }

  try {
    const parsed = JSON.parse(stored);
    const { status: _unusedStatus, createdByStudent: _unusedCreatedBy, ...rest } = parsed || {};

    return {
      ...buildDefaultFilters(),
      ...rest,
    };
  } catch (error) {
    return buildDefaultFilters();
  }
};

const CertificatesArrivalDeparturesList = ({ userHeiID }) => {
  const initialState = {
    academicYear: academicFilter(),
    country: [],
    erasmusCode: "",
    studentLastName: "",
    studentFirstName: "",
    organizationalUnit: "",
    iscedCodeLA: "",
    sortBy:
      localStorage.getItem(LOCAL_STORAGE_KEYS.sortBy) !== null
        ? JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.sortBy))
        : "changed_descending",
    selectedFiltersList: parseStoredFilters(),
  };

  const filters = useOlaFilters(initialState);
  const {
    state,
    setSelectedFiltersList,
    setExpandFilters,
    setSortBy,
  } = filters;
  const {
    selectedFiltersList,
    sortBy,
    expandFilters,
  } = state;
  const [page, setPage] = useState(
    localStorage.getItem(LOCAL_STORAGE_KEYS.page) !== null
      ? parseInt(JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.page)))
      : 0
  );
  const { t } = useTranslation();
  const selectAllRef = useRef(null);
  const [selectedRowIds, setSelectedRowIds] = useState(() => new Set());
  const [preparedCertificatePayload, setPreparedCertificatePayload] =
    useState([]);
  const [showCertificatesModal, setShowCertificatesModal] = useState(false);
  const [isIssuing, setIsIssuing] = useState(false);
  const [isPreparingCertificates, setIsPreparingCertificates] = useState(false);
  const apolloClient = useApolloClient();

  const [createCosMutation] = useMutation(CREATE_COS);
  const [updateCosMutation] = useMutation(UPDATE_COS);

  const effectiveFilters = useMemo(() => {
    const base = buildDefaultFilters();
    const { status: _unusedStatus, createdByStudent: _unusedCreatedBy, ...rest } =
      selectedFiltersList || {};
    return {
      ...base,
      ...rest,
    };
  }, [selectedFiltersList]);

  const {
    loading: olaListingLoadingPreliminary,
    error: olaListingErrorPreliminary,
    data: olaListingDataPreliminary,
    refetch: refetchOlaListingPreliminary,
  } = useQuery(GET_OLA_LISTING, {
    variables: {
      typeOfStudent: 0,
      page: page,
      sortBy: sortBy,
      optionalOLAFilters: {
        academicYear: effectiveFilters.academicYear,
        country: effectiveFilters.country,
        erasmusCode: effectiveFilters.erasmusCode,
        studentLastName: effectiveFilters.studentLastName,
        studentFirstName: effectiveFilters.studentFirstName,
        faculty: effectiveFilters.organizationalUnit,
        status: STATUS_OPTIONS[0],
        iscedId: effectiveFilters.iscedCodeLA,
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const {
    loading: olaListingLoadingFinal,
    error: olaListingErrorFinal,
    data: olaListingDataFinal,
    refetch: refetchOlaListingFinal,
  } = useQuery(GET_OLA_LISTING, {
    variables: {
      typeOfStudent: 0,
      page: page,
      sortBy: sortBy,
      optionalOLAFilters: {
        academicYear: effectiveFilters.academicYear,
        country: effectiveFilters.country,
        erasmusCode: effectiveFilters.erasmusCode,
        studentLastName: effectiveFilters.studentLastName,
        studentFirstName: effectiveFilters.studentFirstName,
        faculty: effectiveFilters.organizationalUnit,
        status: STATUS_OPTIONS[1],
        iscedId: effectiveFilters.iscedCodeLA,
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const {
    loading: laCountLoadingPreliminary,
    error: laCountErrorPreliminary,
    data: laCountPreliminary,
    refetch: refetchLaCountPreliminary,
  } = useQuery(GET_LA_COUNT, {
    variables: {
      homeHeiID: "",
      hostHeiID: userHeiID,
      academicYear: effectiveFilters.academicYear,
      status: STATUS_OPTIONS[0],
      studentFirstName: effectiveFilters.studentFirstName,
      studentLastName: effectiveFilters.studentLastName,
      erasmusCode: effectiveFilters.erasmusCode,
      country: effectiveFilters.country,
      faculty: effectiveFilters.organizationalUnit,
      iscedId: effectiveFilters.iscedCodeLA,
    },
    fetchPolicy: "cache-and-network",
  });

  const {
    loading: laCountLoadingFinal,
    error: laCountErrorFinal,
    data: laCountFinal,
    refetch: refetchLaCountFinal,
  } = useQuery(GET_LA_COUNT, {
    variables: {
      homeHeiID: "",
      hostHeiID: userHeiID,
      academicYear: effectiveFilters.academicYear,
      status: STATUS_OPTIONS[1],
      studentFirstName: effectiveFilters.studentFirstName,
      studentLastName: effectiveFilters.studentLastName,
      erasmusCode: effectiveFilters.erasmusCode,
      country: effectiveFilters.country,
      faculty: effectiveFilters.organizationalUnit,
      iscedId: effectiveFilters.iscedCodeLA,
    },
    fetchPolicy: "cache-and-network",
  });

  const {
    loading: shortTermListingLoadingPreliminary,
    error: shortTermListingErrorPreliminary,
    data: shortTermListingDataPreliminary,
    refetch: refetchShortTermListingPreliminary,
  } = useQuery(GET_SHORT_TERM_LISTING, {
    variables: {
      typeOfStudent: 0,
      page: page,
      sortBy: sortBy,
      optionalSTLAFilters: {
        academicYear: effectiveFilters.academicYear,
        country: effectiveFilters.country,
        erasmusCode: effectiveFilters.erasmusCode,
        studentLastName: effectiveFilters.studentLastName,
        studentFirstName: effectiveFilters.studentFirstName,
        faculty: effectiveFilters.organizationalUnit,
        status: STATUS_OPTIONS[0],
        iscedId: effectiveFilters.iscedCodeLA,
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const {
    loading: shortTermListingLoadingFinal,
    error: shortTermListingErrorFinal,
    data: shortTermListingDataFinal,
    refetch: refetchShortTermListingFinal,
  } = useQuery(GET_SHORT_TERM_LISTING, {
    variables: {
      typeOfStudent: 0,
      page: page,
      sortBy: sortBy,
      optionalSTLAFilters: {
        academicYear: effectiveFilters.academicYear,
        country: effectiveFilters.country,
        erasmusCode: effectiveFilters.erasmusCode,
        studentLastName: effectiveFilters.studentLastName,
        studentFirstName: effectiveFilters.studentFirstName,
        faculty: effectiveFilters.organizationalUnit,
        status: STATUS_OPTIONS[1],
        iscedId: effectiveFilters.iscedCodeLA,
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const {
    loading: shortTermCountLoadingPreliminary,
    error: shortTermCountErrorPreliminary,
    data: shortTermCountPreliminary,
    refetch: refetchShortTermCountPreliminary,
  } = useQuery(GET_SHORT_TERM_LA_COUNT, {
    variables: {
      homeHeiID: "",
      hostHeiID: userHeiID,
      academicYear: effectiveFilters.academicYear,
      status: STATUS_OPTIONS[0],
      studentFirstName: effectiveFilters.studentFirstName,
      studentLastName: effectiveFilters.studentLastName,
      erasmusCode: effectiveFilters.erasmusCode,
      country: effectiveFilters.country,
      faculty: effectiveFilters.organizationalUnit,
      iscedId: effectiveFilters.iscedCodeLA,
    },
    fetchPolicy: "cache-and-network",
  });

  const {
    loading: shortTermCountLoadingFinal,
    error: shortTermCountErrorFinal,
    data: shortTermCountFinal,
    refetch: refetchShortTermCountFinal,
  } = useQuery(GET_SHORT_TERM_LA_COUNT, {
    variables: {
      homeHeiID: "",
      hostHeiID: userHeiID,
      academicYear: effectiveFilters.academicYear,
      status: STATUS_OPTIONS[1],
      studentFirstName: effectiveFilters.studentFirstName,
      studentLastName: effectiveFilters.studentLastName,
      erasmusCode: effectiveFilters.erasmusCode,
      country: effectiveFilters.country,
      faculty: effectiveFilters.organizationalUnit,
      iscedId: effectiveFilters.iscedCodeLA,
    },
    fetchPolicy: "cache-and-network",
  });

  const {
    loading: loadingCountries,
    error: errorCountries,
    data: dataCountries,
  } = useQuery(GET_COUNTRIES);

  const handleExpand = (value) => {
    setExpandFilters(value);
  };

  useEffect(() => {
    const storedFilters = localStorage.getItem(LOCAL_STORAGE_KEYS.filters);

    if (storedFilters) {
      const parsedFilters = parseStoredFilters();

      if (!Object.values(parsedFilters || {})?.every((x) => x === "")) {
        setSelectedFiltersList(parsedFilters);
      }
    }
  }, [setSelectedFiltersList]);

  useEffect(() => {
    page !== null &&
      localStorage.setItem(LOCAL_STORAGE_KEYS.page, JSON.stringify(page));
  }, [page]);

  useEffect(() => {
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.filters,
      JSON.stringify(selectedFiltersList)
    );
  }, [selectedFiltersList]);

  useEffect(() => {
    sortBy !== null &&
      localStorage.setItem(LOCAL_STORAGE_KEYS.sortBy, JSON.stringify(sortBy));
  }, [sortBy]);


  const handleRemoveFilter = (filter, value) => {
    if (filter === "country") {
      let temp = Object.assign({}, selectedFiltersList);
      temp[filter] = temp[filter].filter((i) => i !== value);
      setSelectedFiltersList(temp);
    } else {
      let temp = Object.assign({}, selectedFiltersList);
      temp[filter] = "";
      setSelectedFiltersList(temp);
    }
  };

  const formatDate = (string) => {
    var options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(string).toLocaleDateString("en-UK", options);
  };

  const formatDateForApi = (date) => {
    if (!date) {
      return null;
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const combinedListing = useMemo(
    () => [
      ...(olaListingDataPreliminary?.learningAgreements?.learningAgreementList || []),
      ...(olaListingDataFinal?.learningAgreements?.learningAgreementList || []),
      ...(shortTermListingDataPreliminary?.shortTermMobilities?.shortTermList || []),
      ...(shortTermListingDataFinal?.shortTermMobilities?.shortTermList || []),
    ],
    [
      olaListingDataPreliminary,
      olaListingDataFinal,
      shortTermListingDataPreliminary,
      shortTermListingDataFinal,
    ]
  );

  const combinedListingById = useMemo(
    () =>
      new Map(
        combinedListing.map((item) => [
          item.shortTermID
            ? `short-term-${item.shortTermID}`
            : `ola-${item.olaID}`,
          item,
        ])
      ),
    [combinedListing]
  );
  const combinedCount =
    (laCountPreliminary?.laCount || 0) +
    (laCountFinal?.laCount || 0) +
    (shortTermCountPreliminary?.shortTermLACount || 0) +
    (shortTermCountFinal?.shortTermLACount || 0);

  const getRowId = (item) =>
    item.shortTermID ? `short-term-${item.shortTermID}` : `ola-${item.olaID}`;

  const combinedItems = useMemo(
    () => Array.from(combinedListingById.values()),
    [combinedListingById]
  );

  const visibleRowIds = useMemo(
    () => combinedItems.map((item) => getRowId(item)),
    [combinedItems]
  );

  const isAllSelected =
    visibleRowIds.length > 0 &&
    visibleRowIds.every((id) => selectedRowIds.has(id));
  const isSomeSelected =
    visibleRowIds.some((id) => selectedRowIds.has(id)) && !isAllSelected;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = isSomeSelected;
    }
  }, [isSomeSelected, isAllSelected]);

  useEffect(() => {
    const visibleSet = new Set(visibleRowIds);
    setSelectedRowIds((prev) => {
      let changed = false;
      const next = new Set();

      prev.forEach((id) => {
        if (visibleSet.has(id)) {
          next.add(id);
        } else {
          changed = true;
        }
      });

      if (!changed && next.size === prev.size) {
        return prev;
      }

      return next;
    });
  }, [visibleRowIds]);

  const selectedItems = useMemo(
    () => combinedItems.filter((item) => selectedRowIds.has(getRowId(item))),
    [combinedItems, selectedRowIds]
  );

  const handleToggleRow = (rowId) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const handleToggleAll = () => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (isAllSelected) {
        visibleRowIds.forEach((id) => next.delete(id));
      } else {
        visibleRowIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const getMobilityId = (item) =>
    item.OLAnid || item.STLAnid || item.olaID || item.shortTermID || null;

  const fetchExistingCosForItem = async (item) => {
    const omobilityId = getMobilityId(item);

    if (!omobilityId) {
      return null;
    }

    const response = await apolloClient.query({
      query: GET_COSES,
      variables: {
        page: 0,
        optionalCosesFilters: {
          field_omobility_id: String(omobilityId),
        },
      },
      fetchPolicy: "network-only",
    });

    return response?.data?.coses?.data?.[0] || null;
  };

  const buildCertificatePayload = (item, existingCos) => {
    const omobilityId = getMobilityId(item);
    const omobilityIdString = omobilityId !== null ? String(omobilityId) : null;

    return {
      type: item.shortTermID ? "short_term" : "la",
      title: `COS-${omobilityIdString || ""}`,
      activityType: "student-studies",
      omobilityId: omobilityIdString,
      studentFirstName: item.field_student_firstname,
      studentLastName: item.field_student_lastname,
      studentEmail: item.field_student_email,
      studentBirthdate: item.field_student_birthdate,
      studentEsi: item.field_esi,
      academicYear: item.field_academic_year,
      status: item.field_la_status,
      plannedPeriodFrom: item.field_planned_period_from,
      plannedPeriodTo: item.field_planned_period_to,
      sendingInstitution: item.sending_institution,
      sendingInstitutionHei: item.sending_institution_hei,
      sendingInstitutionHeiId: item.sending_institution_hei_id,
      receivingInstitutionHei: item.receiving_institution_hei,
      receivingInstitutionHeiId: item.receiving_institution_hei_id,
      existingCosId: existingCos?.id || null,
      hasCertificate: Boolean(existingCos),
      certificateTitle: existingCos?.title || null,
    };
  };

  const handlePrepareCertificates = async () => {
    if (!selectedItems.length) {
      return;
    }

    setIsPreparingCertificates(true);

    try {
      const enrichedItems = await Promise.all(
        selectedItems.map(async (item) => {
          const existingCos = await fetchExistingCosForItem(item);
          return buildCertificatePayload(item, existingCos);
        })
      );

      setPreparedCertificatePayload(enrichedItems);
      setShowCertificatesModal(true);
    } catch (error) {
      console.error("Failed to resolve existing COS records", error);
      toast.error("Failed to load certificate status.", toastConfiguration);
    } finally {
      setIsPreparingCertificates(false);
    }
  };

  const handleIssueCertificates = async ({
    arrivalDate,
    departureDate,
    comment,
  }) => {
    if (!preparedCertificatePayload.length) {
      return false;
    }

    const toastId = toast.loading(
      "Issuing certificates...",
      toastConfiguration
    );
    const formattedArrivalDate = formatDateForApi(arrivalDate);
    const formattedDepartureDate = formatDateForApi(departureDate);

    setIsIssuing(true);
    try {
      const promises = preparedCertificatePayload.map((item) => {
        const variables = {
          title: item.title,
          activityType: item.activityType,
          arrivalDate: formattedArrivalDate,
          comment: comment || null,
          departureDate: formattedDepartureDate,
          omobilityId: item.omobilityId,
          receivingAcademicYearId: item.academicYear || null,
          receivingHeiId: item.receivingInstitutionHeiId || null,
          sendingAcademicTermEwp: null,
          sendingHeiId: item.sendingInstitutionHeiId || null,
          studentDateOfBirth: item.studentBirthdate || null,
          studentEmail: item.studentEmail || null,
          studentFamilyName: item.studentLastName || null,
          studentGivenNames: item.studentFirstName || null,
          studentGlobalId: item.studentEsi || null,
        };

        if (item.hasCertificate && item.existingCosId) {
          return updateCosMutation({
            variables: {
              cosID: item.existingCosId,
              ...variables,
            },
          });
        }

        return createCosMutation({
          variables,
        });
      });

      const results = await Promise.allSettled(promises);

      const failed = [];
      results.forEach((r, idx) => {
        if (r.status === "rejected") {
          const item = preparedCertificatePayload[idx];
          failed.push({ item, reason: r.reason });
          console.error("createCos failed for", item, r.reason);
        }
      });

      if (failed.length === 0) {
        try {
          toast.update(toastId, {
            ...toastConfiguration,
            render: "Refreshing data...",
            isLoading: true,
          });
          await Promise.all([
            refetchOlaListingPreliminary(),
            refetchOlaListingFinal(),
            refetchLaCountPreliminary(),
            refetchLaCountFinal(),
            refetchShortTermListingPreliminary(),
            refetchShortTermListingFinal(),
            refetchShortTermCountPreliminary(),
            refetchShortTermCountFinal(),
          ]);
          setSelectedRowIds(new Set());
          toast.update(toastId, {
            ...toastConfiguration,
            render: "Certificates issued.",
            type: "success",
            isLoading: false,
          });
        } catch (refetchError) {
          console.error("Failed to refetch certificates data", refetchError);
          toast.update(toastId, {
            ...toastConfiguration,
            render: "Certificates issued, but refresh failed.",
            type: "warning",
            isLoading: false,
          });
        }
        return true;
      }

      // Build a readable list of failed students (limit to first 10)
      const failedNames = failed
        .slice(0, 10)
        .map((f) => `${f.item.studentFirstName || ""} ${f.item.studentLastName || ""}`)
        .join(", ");

      const failureMessage =
        failed.length <= 10
          ? `Failed to issue certificates for: ${failedNames}`
          : `Failed to issue certificates for ${failed.length} students (showing first 10): ${failedNames}`;

      toast.update(toastId, {
        ...toastConfiguration,
        render: failureMessage,
        type: "error",
        isLoading: false,
      });

      return false;
    } catch (error) {
      console.error("Unexpected error issuing certificates", error);
      toast.update(toastId, {
        ...toastConfiguration,
        render: "Failed to issue certificates.",
        type: "error",
        isLoading: false,
      });
      return false;
    } finally {
      setIsIssuing(false);
    }
  };

  const isLoading =
    olaListingLoadingPreliminary ||
    olaListingLoadingFinal ||
    shortTermListingLoadingPreliminary ||
    shortTermListingLoadingFinal ||
    loadingCountries ||
    laCountLoadingPreliminary ||
    laCountLoadingFinal ||
    shortTermCountLoadingPreliminary ||
    shortTermCountLoadingFinal;

  const hasError =
    olaListingErrorPreliminary ||
    olaListingErrorFinal ||
    shortTermListingErrorPreliminary ||
    shortTermListingErrorFinal ||
    errorCountries ||
    laCountErrorPreliminary ||
    laCountErrorFinal ||
    shortTermCountErrorPreliminary ||
    shortTermCountErrorFinal;

  let content = null;

  if (isLoading) {
    content = (
      <TableSkeleton
        columnsPerFieldMap={{
          first_name: "col-2",
          last_name: "col-2",
          planned_period: "col-2",
          sending_institution: "col-3",
        }}
      />
    );
  } else if (hasError) {
    content = (
      <div className="text-center text-danger mx-auto">
        {t("errorMessage")}
      </div>
    );
  } else {
    content = (
      <main className="h-auto text-center mt-4">
        <div className="col-12 pt-0 pl-3 pr-0 mx-auto">
          <div className="mx-1 mx-xxl-5 certificates-actions-top">
            <CustomButton
              buttonType="orange"
              text="Issue Certificate"
              handleClick={handlePrepareCertificates}
              disabled={selectedRowIds.size === 0 || isPreparingCertificates || isIssuing}
            />
          </div>
        </div>
        <CustomTable
          data={
            combinedItems.map((item) => {
              const rowId = getRowId(item);
              const isSelected = selectedRowIds.has(rowId);
              return {
                select: (
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={isSelected}
                    onChange={() => handleToggleRow(rowId)}
                    aria-label="Select row"
                  />
                ),
                id: rowId,
                first_name: item.field_student_firstname,
                last_name: item.field_student_lastname,
                planned_period:
                  formatDate(item.field_planned_period_from) +
                  " - " +
                  formatDate(item.field_planned_period_to),
                sending_institution: (
                  <div
                    dangerouslySetInnerHTML={{ __html: item.sending_institution }}
                  />
                ),
              };
            })
          }
          page={page}
          setPage={(p) => setPage(p)}
          type={"ola-typeOfStudent-certificates-arrival-departures"}
          expandFilters={expandFilters}
          setExpandFilters={(e) => setExpandFilters(e)}
          filters={selectedFiltersList}
          setFilters={(filter, value) => {
            handleRemoveFilter(filter, value);
            setPage(0);
          }}
          sortBy={sortBy}
          setSortBy={() => setSortBy("")}
          rowClassName="reusable-components-table-row-certificates"
          headerClassName="reusable-components-table-header-certificates"
          columnsPerFieldMap={{
            select: "col-1",
            first_name: "col-2",
            last_name: "col-2",
            planned_period: "col-2",
            sending_institution: "col-3",
          }}
          renderHeaderCell={(label, fieldToHeader) =>
            label === "select" ? (
              <input
                ref={selectAllRef}
                type="checkbox"
                className="form-check-input"
                checked={isAllSelected}
                onChange={handleToggleAll}
                aria-label="Select all"
              />
            ) : (
              fieldToHeader(label)
            )
          }
          pageLimit={30}
          totalCount={combinedCount}
          hasPagination={true}
          hasFilters={true}
        >
          {combinedItems.map((item) => (
            <div
              key={getRowId(item)}
              className="py-3 px-2 justify-content-end justify-content-lg-around align-items-center bg-none bg-md-light button-triplet-requests flex-grow-1 d-flex gap-3 gap-lg-1"
            >
              <ExportCosPdf
                omobilityId={
                  getMobilityId(item) !== null
                    ? String(getMobilityId(item))
                    : null
                }
              />
            </div>
          ))}
        </CustomTable>
        <Filters
          expand={expandFilters}
          handleExpand={handleExpand}
          selectedFiltersList={selectedFiltersList}
          setSelectedFiltersList={(e) => {
            setSelectedFiltersList(e);
            setPage(0);
          }}
          sortBy={sortBy}
          setSortBy={(e) => setSortBy(e)}
          countries={dataCountries?.countries}
          type="ola"
          hideStatus={true}
          hideCreatedBy={true}
        />
        <CertificatesModal
          show={showCertificatesModal}
          onClose={() => setShowCertificatesModal(false)}
          items={preparedCertificatePayload}
          formatDate={formatDate}
          onIssue={handleIssueCertificates}
          isIssuing={isIssuing}
        />
      </main>
    );
  }

  return (
    <>
      {content}
      <ToastContainer />
    </>
  );
};

export default CertificatesArrivalDeparturesList;
